/**
 * useKeyboardShortcuts Hook
 * Keyboard navigation and code application shortcuts for the Workbench
 */

import { useEffect, useCallback } from 'react';

/**
 * Keyboard shortcuts hook
 * @param {Object} options - Configuration options
 * @param {Function} options.onNextChunk - Navigate to next chunk
 * @param {Function} options.onPrevChunk - Navigate to previous chunk
 * @param {Function} options.onNextDocument - Navigate to next document
 * @param {Function} options.onPrevDocument - Navigate to previous document
 * @param {Function} options.onApplyCode - Apply a code by shortcut
 * @param {Function} options.onShowShortcuts - Show shortcuts modal
 * @param {Function} options.onFocusSearch - Focus the code search input
 * @param {Function} options.onEscape - Handle escape key
 * @param {Function} options.onAcceptSuggestion - Accept first AI suggestion (A)
 * @param {Function} options.onRejectSuggestion - Reject first AI suggestion (X)
 * @param {Function} options.onFetchAISuggestions - Fetch AI suggestions (G)
 * @param {Array} options.aiSuggestions - Current AI suggestions array
 * @param {Array} options.codesByShortcut - Map of shortcut -> code for quick apply
 * @param {boolean} options.enabled - Whether shortcuts are enabled
 */
// eslint-disable-next-line max-lines-per-function
export function useKeyboardShortcuts({
  onNextChunk,
  onPrevChunk,
  onNextDocument,
  onPrevDocument,
  onApplyCode,
  onShowShortcuts,
  onFocusSearch,
  onEscape,
  onAcceptSuggestion,
  onRejectSuggestion,
  onFetchAISuggestions,
  aiSuggestions = [],
  codesByShortcut = {},
  enabled = true,
}) {
  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      // Ignore if user is typing in an input or textarea
      const target = event.target;
      const tagName = target.tagName.toLowerCase();
      const isEditable =
        tagName === 'input' ||
        tagName === 'textarea' ||
        target.isContentEditable;

      // Allow Escape and some shortcuts even in inputs
      if (event.key === 'Escape') {
        event.preventDefault();
        if (isEditable) {
          target.blur();
        }
        onEscape?.();
        return;
      }

      // Block other shortcuts in editable fields
      if (isEditable) return;

      const key = event.key.toLowerCase();
      const hasShift = event.shiftKey;
      const hasCtrl = event.ctrlKey || event.metaKey;

      // Search focus: Ctrl/Cmd + F
      if (hasCtrl && key === 'f') {
        event.preventDefault();
        onFocusSearch?.();
        return;
      }

      // Show shortcuts: ?
      if (key === '?' || (hasShift && key === '/')) {
        event.preventDefault();
        onShowShortcuts?.();
        return;
      }

      // Navigation with shift
      if (hasShift) {
        if (key === 'n') {
          event.preventDefault();
          onNextDocument?.();
          return;
        }
        if (key === 'p') {
          event.preventDefault();
          onPrevDocument?.();
          return;
        }
      }

      // Simple navigation
      if (key === 'n' || key === 'enter') {
        event.preventDefault();
        onNextChunk?.();
        return;
      }

      if (key === 'p') {
        event.preventDefault();
        onPrevChunk?.();
        return;
      }

      // AI suggestion shortcuts (use Alt modifier to avoid conflict with code shortcuts)
      // Alt+A = Accept first AI suggestion
      if (key === 'a' && event.altKey && aiSuggestions.length > 0) {
        event.preventDefault();
        onAcceptSuggestion?.(aiSuggestions[0]);
        return;
      }

      // Alt+X = Reject first AI suggestion
      if (key === 'x' && event.altKey && aiSuggestions.length > 0) {
        event.preventDefault();
        onRejectSuggestion?.(aiSuggestions[0]);
        return;
      }

      // Alt+G = Get/Generate AI suggestions
      if (key === 'g' && event.altKey) {
        event.preventDefault();
        onFetchAISuggestions?.({ force: true });
        return;
      }

      // Number keys for code shortcuts (1-9)
      if (/^[1-9]$/.test(key)) {
        const code = codesByShortcut[key];
        if (code) {
          event.preventDefault();
          onApplyCode?.(code.code, { advanceToNext: true });
        }
        return;
      }

      // Letter shortcuts for codes (configured in code registry)
      if (/^[a-z]$/.test(key) && !hasCtrl && !hasShift) {
        const code = codesByShortcut[key.toUpperCase()];
        if (code) {
          event.preventDefault();
          onApplyCode?.(code.code, { advanceToNext: true });
        }
      }
    },
    [
      enabled,
      onNextChunk,
      onPrevChunk,
      onNextDocument,
      onPrevDocument,
      onApplyCode,
      onShowShortcuts,
      onFocusSearch,
      onEscape,
      onAcceptSuggestion,
      onRejectSuggestion,
      onFetchAISuggestions,
      aiSuggestions,
      codesByShortcut,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);

  return null;
}

/**
 * Build a map of shortcuts to codes from the code array
 * @param {Array} codes - Array of behavioral codes
 * @returns {Object} Map of shortcut -> code
 */
export function buildShortcutMap(codes) {
  const map = {};

  codes.forEach((code) => {
    if (code.shortcut) {
      map[code.shortcut] = code;
    }
  });

  return map;
}

export default useKeyboardShortcuts;
